import { Provider } from '@angular/core';
import { injectDispatch } from '@ngrx/signals/events';
import { TransactionEvents } from '@app/domain/events';
import { PlannedExpenseDomainService } from '@app/domain/services';
import {
  GetPlannedExpensesUseCase,
  GetDuePlannedExpensesUseCase,
  GetPlannedExpenseByIdUseCase,
  CountPlannedExpensesUseCase,
  CreatePlannedExpenseUseCase,
  UpdatePlannedExpenseUseCase,
  DeletePlannedExpenseUseCase,
  DeactivatePlannedExpenseUseCase,
  ActivatePlannedExpenseUseCase,
  ActivateAllPlannedExpensesUseCase,
  GetCurrencyRateUseCase,
} from '@app/domain/use-cases';
import type { IPlannedExpenseRepository, IExpenseRepository, ITransactionManager } from '@app/domain/ports';
import {
  PLANNED_EXPENSE_REPOSITORY,
  EXPENSE_REPOSITORY,
  TRANSACTION_MANAGER,
} from '../../tokens/infrastructure.tokens';

/**
 * Provides PlannedExpense domain services and use cases.
 *
 * Includes:
 * - PlannedExpenseDomainService: Business rules and validation
 * - 8 Use Cases: GetPlannedExpenses, GetById, Count, Create, Update, Delete, Deactivate, Activate
 *
 * All providers use constructor injection pattern following Clean Architecture.
 */
export function providePlannedExpenseDomain(): Provider[] {
  return [
    // Domain Service
    {
      provide: PlannedExpenseDomainService,
      useFactory: () => new PlannedExpenseDomainService(),
      deps: [],
    },

    // Use Cases - Query
    {
      provide: GetPlannedExpensesUseCase,
      useFactory: (repository: IPlannedExpenseRepository) => new GetPlannedExpensesUseCase(repository),
      deps: [PLANNED_EXPENSE_REPOSITORY],
    },
    {
      provide: GetDuePlannedExpensesUseCase,
      useFactory: (repository: IPlannedExpenseRepository) => new GetDuePlannedExpensesUseCase(repository),
      deps: [PLANNED_EXPENSE_REPOSITORY],
    },
    {
      provide: GetPlannedExpenseByIdUseCase,
      useFactory: (repository: IPlannedExpenseRepository) => new GetPlannedExpenseByIdUseCase(repository),
      deps: [PLANNED_EXPENSE_REPOSITORY],
    },
    {
      provide: CountPlannedExpensesUseCase,
      useFactory: (repository: IPlannedExpenseRepository) => new CountPlannedExpensesUseCase(repository),
      deps: [PLANNED_EXPENSE_REPOSITORY],
    },

    // Use Cases - Commands
    {
      provide: CreatePlannedExpenseUseCase,
      useFactory: (repository: IPlannedExpenseRepository, domainService: PlannedExpenseDomainService) => {
        const dispatch = injectDispatch(TransactionEvents);
        return new CreatePlannedExpenseUseCase(repository, domainService, dispatch);
      },
      deps: [PLANNED_EXPENSE_REPOSITORY, PlannedExpenseDomainService],
    },
    {
      provide: UpdatePlannedExpenseUseCase,
      useFactory: (repository: IPlannedExpenseRepository, domainService: PlannedExpenseDomainService) => {
        const dispatch = injectDispatch(TransactionEvents);
        return new UpdatePlannedExpenseUseCase(repository, domainService, dispatch);
      },
      deps: [PLANNED_EXPENSE_REPOSITORY, PlannedExpenseDomainService],
    },
    {
      provide: DeletePlannedExpenseUseCase,
      useFactory: (repository: IPlannedExpenseRepository, domainService: PlannedExpenseDomainService) => {
        const dispatch = injectDispatch(TransactionEvents);
        return new DeletePlannedExpenseUseCase(repository, domainService, dispatch);
      },
      deps: [PLANNED_EXPENSE_REPOSITORY, PlannedExpenseDomainService],
    },
    {
      provide: DeactivatePlannedExpenseUseCase,
      useFactory: (repository: IPlannedExpenseRepository) => {
        const dispatch = injectDispatch(TransactionEvents);
        return new DeactivatePlannedExpenseUseCase(repository, dispatch);
      },
      deps: [PLANNED_EXPENSE_REPOSITORY],
    },
    {
      provide: ActivatePlannedExpenseUseCase,
      useFactory: (
        plannedExpenseRepository: IPlannedExpenseRepository,
        expenseRepository: IExpenseRepository,
        getCurrencyRateUseCase: GetCurrencyRateUseCase,
        transactionManager: ITransactionManager,
      ) => {
        const dispatch = injectDispatch(TransactionEvents);
        return new ActivatePlannedExpenseUseCase(
          plannedExpenseRepository,
          expenseRepository,
          getCurrencyRateUseCase,
          transactionManager,
          dispatch,
        );
      },
      deps: [PLANNED_EXPENSE_REPOSITORY, EXPENSE_REPOSITORY, GetCurrencyRateUseCase, TRANSACTION_MANAGER],
    },
    {
      provide: ActivateAllPlannedExpensesUseCase,
      useFactory: (
        plannedExpenseRepository: IPlannedExpenseRepository,
        expenseRepository: IExpenseRepository,
        getCurrencyRateUseCase: GetCurrencyRateUseCase,
        transactionManager: ITransactionManager,
      ) => {
        const dispatch = injectDispatch(TransactionEvents);
        return new ActivateAllPlannedExpensesUseCase(
          plannedExpenseRepository,
          expenseRepository,
          getCurrencyRateUseCase,
          transactionManager,
          dispatch,
        );
      },
      deps: [PLANNED_EXPENSE_REPOSITORY, EXPENSE_REPOSITORY, GetCurrencyRateUseCase, TRANSACTION_MANAGER],
    },
  ];
}
